import React from "react";
import {
  Autocomplete,
  Box,
  Button,
  Card,
  Chip,
  IconButton,
  Modal,
  ModalClose,
  ModalDialog,
  Stack,
  Typography,
} from "@mui/joy";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ListIcon from "@mui/icons-material/List";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";

// Country to flag emoji mapping
const countryFlags: Record<string, string> = {
  Japan: "🇯🇵",
  Spain: "🇪🇸",
  France: "🇫🇷",
  USA: "🇺🇸",
  "United States": "🇺🇸",
  UK: "🇬🇧",
  "United Kingdom": "🇬🇧",
  Argentina: "🇦🇷",
  Canada: "🇨🇦",
  Italy: "🇮🇹",
  Mexico: "🇲🇽",
  Germany: "🇩🇪",
  Australia: "🇦🇺",
  Brazil: "🇧🇷",
  China: "🇨🇳",
  India: "🇮🇳",
  Thailand: "🇹🇭",
  Vietnam: "🇻🇳",
  Indonesia: "🇮🇩",
  Singapore: "🇸🇬",
  Malaysia: "🇲🇾",
  Philippines: "🇵🇭",
  "South Korea": "🇰🇷",
  Netherlands: "🇳🇱",
  Belgium: "🇧🇪",
  Switzerland: "🇨🇭",
  Austria: "🇦🇹",
  Portugal: "🇵🇹",
  Greece: "🇬🇷",
  Turkey: "🇹🇷",
  Egypt: "🇪🇬",
  "South Africa": "🇿🇦",
  Morocco: "🇲🇦",
  Kenya: "🇰🇪",
  "New Zealand": "🇳🇿",
  Ireland: "🇮🇪",
  Scotland: "🏴󠁧󠁢󠁳󠁣󠁴󠁿",
  Norway: "🇳🇴",
  Sweden: "🇸🇪",
  Denmark: "🇩🇰",
  Finland: "🇫🇮",
  Iceland: "🇮🇸",
  Poland: "🇵🇱",
  "Czech Republic": "🇨🇿",
  Hungary: "🇭🇺",
  Croatia: "🇭🇷",
  Russia: "🇷🇺",
  Peru: "🇵🇪",
  Chile: "🇨🇱",
  Colombia: "🇨🇴",
  "Costa Rica": "🇨🇷",
  Cuba: "🇨🇺",
  Jamaica: "🇯🇲",
  "Puerto Rico": "🇵🇷",
  Israel: "🇮🇱",
  Jordan: "🇯🇴",
  "United Arab Emirates": "🇦🇪",
  Dubai: "🇦🇪",
  Qatar: "🇶🇦",
  "Saudi Arabia": "🇸🇦",
  Taiwan: "🇹🇼",
  "Hong Kong": "🇭🇰",
};

// Deduplicated country list for autocomplete (remove aliases like "USA"/"United States")
const COUNTRY_ALIASES: Record<string, string> = {
  USA: "United States",
  UK: "United Kingdom",
  Dubai: "United Arab Emirates",
};

const allCountryOptions = Object.keys(countryFlags).filter(
  (c) => !COUNTRY_ALIASES[c]
);

// Vibrant colors for stamp cards
const stampColors = [
  "#FF9800",
  "#E91E63",
  "#2196F3",
  "#4CAF50",
  "#9C27B0",
  "#00BCD4",
  "#FF5722",
  "#3F51B5",
  "#009688",
  "#FFC107",
];

interface TravelStampsProps {
  countries: string[];
  isOwnProfile: boolean;
  manualCountries: string[];
  onAddCountry?: (country: string) => void;
  onRemoveCountry?: (country: string) => void;
}

export const TravelStamps = ({
  countries,
  isOwnProfile,
  manualCountries,
  onAddCountry,
  onRemoveCountry,
}: TravelStampsProps) => {
  const [viewMode, setViewMode] = React.useState<"stamps" | "list">("stamps");
  const [modalOpen, setModalOpen] = React.useState(false);
  const [selectedCountry, setSelectedCountry] = React.useState<string | null>(
    null
  );

  const availableCountries = allCountryOptions.filter(
    (c) => !countries.includes(c)
  );

  const handleAddCountry = () => {
    if (selectedCountry && onAddCountry) {
      onAddCountry(selectedCountry);
      setSelectedCountry(null);
      setModalOpen(false);
    }
  };

  const isManualCountry = (country: string) =>
    manualCountries.includes(country);

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        marginBottom={2}
      >
        <Box>
          <Typography level="h4">Your travel stamps</Typography>
          <Typography level="body-sm" sx={{ color: "text.secondary" }}>
            A passport full of memories
          </Typography>
        </Box>
        <Stack direction="row" spacing={0.5}>
          {isOwnProfile && (
            <IconButton
              variant="outlined"
              size="sm"
              onClick={() => setModalOpen(true)}
              aria-label="add country"
              data-testid="add-country-button"
              sx={{ minWidth: "auto" }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          )}
          <Button
            variant={viewMode === "stamps" ? "solid" : "outlined"}
            size="sm"
            onClick={() => setViewMode("stamps")}
            sx={{ minWidth: "auto", px: 1 }}
          >
            <ViewModuleIcon fontSize="small" />
          </Button>
          <Button
            variant={viewMode === "list" ? "solid" : "outlined"}
            size="sm"
            onClick={() => setViewMode("list")}
            sx={{ minWidth: "auto", px: 1 }}
          >
            <ListIcon fontSize="small" />
          </Button>
        </Stack>
      </Stack>

      {countries.length === 0 ? (
        <Card
          variant="outlined"
          sx={{
            padding: 4,
            textAlign: "center",
            backgroundColor: "background.level1",
          }}
        >
          <Typography level="body-sm" sx={{ color: "text.tertiary" }}>
            No countries visited yet. Start exploring!
          </Typography>
        </Card>
      ) : viewMode === "stamps" ? (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: 1.5,
          }}
        >
          {countries.map((country, idx) => (
            <Card
              key={country}
              sx={{
                backgroundColor: stampColors[idx % stampColors.length],
                color: "white",
                padding: 2,
                textAlign: "center",
                borderRadius: "lg",
                aspectRatio: "1",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
                "&:hover .remove-country-btn": {
                  opacity: 1,
                },
              }}
            >
              {isOwnProfile && isManualCountry(country) && (
                <IconButton
                  className="remove-country-btn"
                  size="sm"
                  variant="solid"
                  color="danger"
                  onClick={() => onRemoveCountry?.(country)}
                  aria-label={`remove ${country}`}
                  data-testid={`remove-country-${country}`}
                  sx={{
                    position: "absolute",
                    top: 4,
                    right: 4,
                    minWidth: "auto",
                    minHeight: "auto",
                    padding: "2px",
                    opacity: 0,
                    transition: "opacity 0.2s",
                  }}
                >
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
              )}
              <Typography level="h2" sx={{ mb: 0.5 }}>
                {countryFlags[country] || "🏳️"}
              </Typography>
              <Typography
                level="body-xs"
                sx={{
                  color: "white",
                  fontWeight: "bold",
                  textTransform: "uppercase",
                  fontSize: "0.65rem",
                }}
              >
                {country}
              </Typography>
            </Card>
          ))}
        </Box>
      ) : (
        <Stack spacing={1}>
          {countries.map((country) => (
            <Stack
              key={country}
              direction="row"
              alignItems="center"
              spacing={1}
            >
              <Chip variant="soft" size="lg" sx={{ flex: 1 }}>
                {countryFlags[country] || "🏳️"} {country}
              </Chip>
              {isOwnProfile && isManualCountry(country) && (
                <IconButton
                  size="sm"
                  variant="plain"
                  color="danger"
                  onClick={() => onRemoveCountry?.(country)}
                  aria-label={`remove ${country}`}
                  data-testid={`remove-country-${country}`}
                >
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              )}
            </Stack>
          ))}
        </Stack>
      )}

      {/* Add Country Modal */}
      <Modal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSelectedCountry(null);
        }}
      >
        <ModalDialog sx={{ minWidth: 300 }}>
          <ModalClose />
          <Typography level="h4" sx={{ mb: 2 }}>
            Add a country
          </Typography>
          <Autocomplete
            placeholder="Search countries..."
            options={availableCountries}
            value={selectedCountry}
            onChange={(_event, newValue) => setSelectedCountry(newValue)}
            getOptionLabel={(option) =>
              `${countryFlags[option] || "🏳️"} ${option}`
            }
            data-testid="country-autocomplete"
            sx={{ mb: 2 }}
          />
          <Button
            onClick={handleAddCountry}
            disabled={!selectedCountry}
            data-testid="confirm-add-country"
          >
            Add Country
          </Button>
        </ModalDialog>
      </Modal>
    </Box>
  );
};
